package ui.app.assign.detail.wheel.editor.pages

import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.VerticalScrollbar
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.rememberScrollbarAdapter
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import api.ResourceOrientation
import apps.App.Wheel.Theme.Companion.ORIENTATION_LAND
import apps.App.Wheel.Theme.Companion.ORIENTATION_PORT
import common.frontend.api.Io
import compose.assets.Svg
import compose.assets.svg.Arrow_left
import compose.assets.svg.Device
import compose.assets.svg.Format_text
import compose.assets.svg.Text_long
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus
import org.ssoft.common.tools.nowDate
import ssoft.frontend.generated.resources.*
import tools.EFieldRegularInt
import tools.rememberString
import ui.app.assign.detail.definition.AppSchedulerField
import ui.app.assign.detail.wheel.editor.AppEditorWheelScope
import ui.app.assign.detail.wheel.editor.widgets.FieldCustomImage
import ui.widget.Dialog
import ui.widget.DialogState
import ui.widget.DialogUI
import ui.widget.DropDownPickerCard
import kotlin.time.Clock


@Composable
fun AppEditorWheelScope.AppWheelPage01_Info(nnvl: AppEditorWheelScope.Wrapper) {
    val state = rememberLazyListState()
    Box(Modifier.fillMaxSize()) {
        LazyColumn(
            state = state,
            modifier = Modifier.fillMaxSize(),
            contentPadding = PaddingValues(16.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            item {
                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    ContentInfo(nnvl, display.isMobileOrSmall)
                    DeviceSelector(
                        nnvl = nnvl,
                        modifier = if (display.isMobileOrSmall) Modifier.fillMaxWidth()
                        else Modifier.width(512.dp).align(Alignment.CenterHorizontally)
                    )
                    ButtonNext(Modifier.align(Alignment.CenterHorizontally)) {
                        when {
                            nnvl.devices.state.none { it.isActive.state } -> {
                                showMessage(Res.string.layer_bottom_wheel_validation_device)
                            }

                            nnvl.scheduled.state && nnvl.end.state < Clock.nowDate() -> {
                                showMessage(Res.string.layer_bottom_wheel_validation_dt_range)
                            }

                            else -> true
                        }
                    }
                }
            }
        }
        VerticalScrollbar(
            modifier = Modifier.fillMaxHeight()
                .align(Alignment.CenterEnd)
                .padding(4.dp),
            adapter = rememberScrollbarAdapter(state)
        )
    }
}

@Composable
private fun AppEditorWheelScope.ContentInfo(
    nnvl: AppEditorWheelScope.Wrapper,
    mobileVersion: Boolean
) {
    val logoFieldFun: @Composable (Modifier) -> Unit = { modifier ->
        FieldCustomImage(
            companyId = args.companyId,
            entryId = args.appId,
            canEdit = true,
            image = nnvl.logo,
            label = Res.string.layer_bottom_detail__pick_app_icon_label,
            orientation = ResourceOrientation.UNDEFINED,
            small = false,
            extraKeyImage = "logo:${args.appId}",
            canBeIcon = true,
            modifier = modifier.let {
                if (display.isMobileOrSmall) it.padding(horizontal = 16.dp)
                else it
            },
            cardModifier = Modifier.let {
                if (display.isMobileOrSmall) it.fillMaxWidth().aspectRatio(1F)
                else it.size((256).dp)
            }
        )
    }
    Card(
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceContainer
        )
    ) {

        Row(
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            modifier = Modifier.padding(16.dp)
        ) {
            if (!mobileVersion) logoFieldFun(Modifier)

            Column(
                verticalArrangement = Arrangement.spacedBy(8.dp),
                modifier = Modifier.let {
                    if (mobileVersion) it.fillMaxWidth()
                    else it.width(384.dp)
                }) {
                TextField(
                    readOnly = !nnvl.canEdit,
                    value = nnvl.name.text,
                    isError = nnvl.name.err,
                    onValueChange = { nnvl.name.set(it) },
                    modifier = Modifier.fillMaxWidth(),
                    leadingIcon = { Icon(Svg.Format_text, "") },
                    label = { Text(Res.string.layer_bottom_editor__label_name.rememberString()) })

                TextField(
                    readOnly = !nnvl.canEdit,
                    value = nnvl.note.text,
                    isError = nnvl.note.err,
                    onValueChange = { nnvl.note.set(it) },
                    modifier = Modifier.fillMaxWidth(),
                    leadingIcon = { Icon(Svg.Text_long, "") },
                    label = { Text(Res.string.layer_bottom_editor__label_note.rememberString()) })

                AppOrientation(
                    readOnly = !nnvl.canEdit,
                    orientation = nnvl.orientation,
                    nnvl = nnvl
                )
                AppSchedulerField(
                    canEdit = nnvl.canEdit,
                    scheduled = nnvl.scheduled,
                    start = nnvl.start,
                    end = nnvl.end,
                    enabledRange = LocalDate(1, 1, 1) to Clock.nowDate().plus(DatePeriod(years = 2)),
                    modifier = Modifier.fillMaxWidth()
                )
            }
        }
        if (mobileVersion) {
            logoFieldFun(
                Modifier.align(Alignment.CenterHorizontally)
                    .padding(bottom = 16.dp)
            )
        }
    }
}

@Composable
private fun AppEditorWheelScope.AppOrientation(
    readOnly: Boolean,
    orientation: EFieldRegularInt,
    nnvl: AppEditorWheelScope.Wrapper
) {
    DropDownPickerCard(
        selected = { AppEditorWheelScope.AppOrientation.all.first { it.orientation == orientation.state } },
        values = AppEditorWheelScope.AppOrientation.all,
        itemWidth = if (display.isMobileOrSmall) Dp.Unspecified else 384.dp,
        modifier = Modifier.fillMaxWidth(),
        enabled = !readOnly,
        shape = RoundedCornerShape(4.dp, 4.dp),
        useItemWidthForCardWidth = true
    ) {
        orientation.set(it.orientation)
        nnvl.devices.state
            .firstOrNull { device -> device.isActive.state }
            ?.takeIf { item -> item.data.isLand != (it.orientation == ORIENTATION_LAND) }
            ?.also { it.isActive.set(false) }
    }
}

@Composable
private fun AppEditorWheelScope.DeviceSelector(
    nnvl: AppEditorWheelScope.Wrapper,
    modifier: Modifier
) {
    val selectorDialog = DialogState.remember(key = "DeviceSelector")
    DeviceSelectorDialog(nnvl, selectorDialog) {
        nnvl.devices.state
            .firstOrNull { device -> device.data.id == it.id }
            ?.also {
                it.isActive.set(true)
                if (it.data.isLand != (nnvl.orientation.state == ORIENTATION_LAND)) {
                    if (it.data.isLand) nnvl.orientation.set(ORIENTATION_LAND)
                    else nnvl.orientation.set(ORIENTATION_PORT)
                }
            }
    }
    Card(
        onClick = { selectorDialog.show() },
        modifier = modifier
    ) {
        Row(
            modifier = Modifier.padding(16.dp),
            horizontalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            Icon(
                imageVector = Svg.Device,
                contentDescription = null,
                modifier = Modifier.align(Alignment.CenterVertically)
            )
            nnvl.devices.state
                .firstOrNull { it.isActive.state }
                ?.also { Text(it.data.name) }
                ?: Text(
                    text = Res.string.layer_bottom_editor__label_device_title.rememberString(),
                    modifier = Modifier.align(Alignment.CenterVertically)
                )
            Spacer(Modifier.weight(1F))
            Icon(
                imageVector = Svg.Arrow_left,
                contentDescription = null,
                modifier = Modifier.align(Alignment.CenterVertically)
                    .rotate(180F)
            )
        }
    }
}

private val Io.AppDetail.DeviceItem.isLand get() = width > height

@OptIn(ExperimentalFoundationApi::class)
@Composable
private fun DeviceSelectorDialog(
    nnvl: AppEditorWheelScope.Wrapper,
    selectorDialog: DialogState,
    onSelected: (Io.AppDetail.DeviceItem) -> Unit
) {
    Dialog(selectorDialog) {
        val screenLand = nnvl.orientation.state == ORIENTATION_LAND
        val devicesExists = remember {
            nnvl.data.locations.any {
                it.devices.isNotEmpty()
            }
        }
        val locations = remember {
            nnvl.data.locations
                .map {
                    it.name to it.devices.filter { it.isLand == screenLand }
                        .sortedBy { it.name }
                }
                .filter { it.second.isNotEmpty() }
                .sortedBy { it.first }

        }
        DialogUI(
            modifier = Modifier.width(384.dp).align(Alignment.Center),
            onDismissRequest = { selectorDialog.hide() },
            title = { Text(Res.string.layer_bottom_editor__label_device_title.rememberString()) },
            dismissButton = {
                TextButton({ selectorDialog.hide() }) {
                    Text(Res.string.dialog__action_close.rememberString())
                }
            }) {
            if (!devicesExists) {
                Text(
                    overflow = TextOverflow.Ellipsis,
                    text = Res.string.layer_bottom_editor__label_device_err_no_devices_by_layer.rememberString(),
                    modifier = Modifier.padding(16.dp)
                )
            } else if (devicesExists && locations.isEmpty()) {
                Text(
                    overflow = TextOverflow.Ellipsis,
                    text = Res.string.layer_bottom_editor__label_device_err_no_devices_by_rotation.rememberString(),
                    modifier = Modifier.padding(16.dp)
                )
            } else {
                LazyColumn(
                    Modifier.fillMaxWidth()
                        .height(
                            minOf(
                                a = 512,
                                b = (locations.size * 24) + (locations.sumOf { it.second.size } * 64)
                            ).dp),
                ) {
                    locations.forEach { item ->
                        stickyHeader {
                            Text(
                                text = item.first,
                                overflow = TextOverflow.Ellipsis,
                                maxLines = 1,
                                style = MaterialTheme.typography.labelMedium,
                                modifier = Modifier
                                    .background(MaterialTheme.colorScheme.surfaceContainerHigh)
                                    .fillMaxWidth()
                                    .padding(4.dp)
                            )
                        }
                        items(item.second) {
                            Button(
                                modifier = Modifier.fillMaxWidth(),
                                onClick = {
                                    onSelected(it)
                                    selectorDialog.hide()
                                }) {
                                Text(
                                    text = it.name,
                                    overflow = TextOverflow.Ellipsis,
                                    maxLines = 1,
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}